import { MODULE_CATALOG, type ModuleKey } from '@alicerce/contracts';
import { Alert, Badge, Card, CardBody, CardHeader, cn, formatBRL, formatDate, formatNumber, PageHeader, PageLoader, Stat, Tabs } from '@alicerce/ui';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, Circle } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { errorMessage } from '@/lib/api';
import { useApi, useCurrent } from '@/lib/session';

export interface Widget {
  key: string;
  moduleKey: ModuleKey;
  title: string;
  kind: 'metric' | 'list' | 'chart' | 'checklist' | 'upsell';
  data: Record<string, unknown>;
}

interface DashboardResponse {
  range: { key: string; from: string; to: string };
  widgets: Widget[];
  failed: string[];
}

type Range = 'today' | 'week' | 'month';
const RANGES: { key: Range; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'week', label: 'Semana' },
  { key: 'month', label: 'Mês' },
];

function WidgetLink({ to, children }: { to: string; children: React.ReactNode }) {
  return to.startsWith('http') ? (
    <a href={to} className="text-sm font-medium text-brand-700 hover:underline" target="_blank" rel="noreferrer">
      {children}
    </a>
  ) : (
    <Link to={to} className="text-sm font-medium text-brand-700 hover:underline">
      {children}
    </Link>
  );
}

function Checklist({ items }: { items: { key: string; label: string; done: boolean; link?: string }[] }) {
  const done = items.filter((i) => i.done).length;
  return (
    <div>
      <div className="mb-3 h-2 overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuemin={0} aria-valuemax={items.length} aria-valuenow={done} aria-label="Configuração concluída">
        <div className="h-full bg-brand-600" style={{ width: `${items.length ? (done / items.length) * 100 : 0}%` }} />
      </div>
      <ul className="space-y-2">
        {items.map((i) => (
          <li key={i.key} className="flex items-center gap-2 text-sm">
            {i.done ? <CheckCircle2 className="size-4 text-success" aria-label="Feito" /> : <Circle className="size-4 text-slate-300" aria-label="Pendente" />}
            {i.link && !i.done ? (
              <WidgetLink to={i.link}>{i.label}</WidgetLink>
            ) : (
              <span className={cn(i.done && 'text-muted line-through')}>{i.label}</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface SetupStep {
  key: string;
  label: string;
  description: string;
  done: boolean;
  link: string;
}

/** Passo a passo da agenda: cada passo explica o que fazer e o próximo pendente leva até a tela certa. */
function AgendaSetup({ steps }: { steps: SetupStep[] }) {
  const next = steps.find((s) => !s.done);
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted">Siga os passos para os clientes marcarem horário pela loja.</p>
      <ol className="space-y-3" aria-label="Passos para configurar a agenda">
        {steps.map((s, i) => (
          <li key={s.key} className="flex gap-3">
            <span
              aria-hidden
              className={cn(
                'grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold',
                s.done ? 'bg-success/10 text-success' : s === next ? 'bg-brand-600 text-white' : 'bg-mist text-muted',
              )}
            >
              {s.done ? <CheckCircle2 className="size-4" /> : i + 1}
            </span>
            <div className="min-w-0 text-sm">
              <p className={cn('font-medium', s.done && 'text-muted line-through')}>
                {s.label}
                <span className="sr-only">{s.done ? ' (feito)' : ' (pendente)'}</span>
              </p>
              {!s.done && <p className="mt-0.5 text-muted">{s.description}</p>}
              {s === next && (
                <Link to={s.link} className="mt-2 inline-flex rounded-full bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-800">
                  Fazer agora
                </Link>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Corpo de cada widget conhecido; um widget novo da API sem tela aparece com título e um resumo. */
export function WidgetBody({ w }: { w: Widget }) {
  const d = w.data as Record<string, never>;
  switch (w.key) {
    case 'core.checklist':
      return <Checklist items={d.items ?? []} />;
    case 'agenda.setup':
      return <AgendaSetup steps={d.steps ?? []} />;
    case 'checkout.sales':
      return <Stat label="Vendido no período" value={formatBRL(d.valueCents)} hint={`${formatNumber(d.orders)} pedido(s) pago(s)`} />;
    case 'checkout.to_ship':
      return (
        <div className="space-y-2">
          <Stat label="Pagos esperando envio" value={formatNumber(d.value)} />
          <WidgetLink to={String(d.link ?? '/pedidos')}>Ver pedidos</WidgetLink>
        </div>
      );
    case 'finance.month':
      return (
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Entrou" value={formatBRL(d.inCents)} tone="positive" />
          <Stat label="Saiu" value={formatBRL(d.outCents)} tone="negative" />
          <Stat label="Sobrou" value={formatBRL(d.leftCents)} tone={Number(d.leftCents) >= 0 ? 'neutral' : 'negative'} />
        </div>
      );
    case 'finance.due': {
      const rows = [
        ...((d.payables ?? []) as { id: string; description: string; amountCents: number; dueDate: string | null }[]).map((r) => ({ ...r, kind: 'Pagar' })),
        ...((d.receivables ?? []) as { id: string; description: string; amountCents: number; dueDate: string | null }[]).map((r) => ({ ...r, kind: 'Receber' })),
      ];
      return rows.length ? (
        <ul className="divide-y divide-line text-sm">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-2 py-2">
              <span className="truncate">
                <Badge tone={r.kind === 'Pagar' ? 'danger' : 'success'}>{r.kind}</Badge> {r.description}
              </span>
              <span className="whitespace-nowrap tabular-nums">
                {formatBRL(r.amountCents)} · {formatDate(r.dueDate)}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">Nenhuma conta vencendo esta semana.</p>
      );
    }
    case 'storefront.bookings_today': {
      const items = (d.items ?? []) as { id: string; time: string; name: string; service: string }[];
      return (
        <div className="space-y-2">
          {items.length ? (
            <ul className="divide-y divide-line text-sm">
              {items.map((i) => (
                <li key={i.id} className="flex gap-3 py-2">
                  <span className="w-16 shrink-0 font-semibold tabular-nums">{i.time}</span>
                  <span className="truncate">
                    {i.name} <span className="text-muted">· {i.service}</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">Ninguém marcou para hoje.</p>
          )}
          <WidgetLink to={String(d.link ?? '/agenda')}>Abrir agenda</WidgetLink>
        </div>
      );
    }
    case 'inventory.low_stock': {
      const items = (d.items ?? []) as { variantId: string; label: string; available: number; minimum: number }[];
      return items.length ? (
        <ul className="divide-y divide-line text-sm">
          {items.map((i) => (
            <li key={i.variantId} className="flex justify-between gap-2 py-2">
              <Link to={`/estoque?variante=${i.variantId}`} className="truncate hover:underline">
                {i.label}
              </Link>
              <span className="whitespace-nowrap text-danger">
                {i.available} (mín. {i.minimum})
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">Nenhum produto abaixo do mínimo.</p>
      );
    }
    case 'fiscal.month':
      return <Stat label="Notas autorizadas" value={formatNumber(d.count)} />;
    case 'fiscal.rejected': {
      const items = (d.items ?? []) as { id: string; message: string | null; link: string }[];
      return items.length ? (
        <ul className="space-y-2 text-sm">
          {items.map((i) => (
            <li key={i.id}>
              <WidgetLink to={i.link}>{i.message ?? 'Nota rejeitada'}</WidgetLink>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">Nenhuma nota para corrigir.</p>
      );
    }
    case 'inbox.unanswered': {
      const items = (d.items ?? []) as { id: string; name: string; since: string | null; link: string }[];
      return items.length ? (
        <ul className="divide-y divide-line text-sm">
          {items.map((i) => (
            <li key={i.id} className="flex justify-between gap-2 py-2">
              <WidgetLink to={i.link}>{i.name}</WidgetLink>
              <span className="text-muted">{formatDate(i.since)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted">Todas as conversas respondidas.</p>
      );
    }
    case 'ads.results':
      return (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Gasto" value={formatBRL(d.spendCents)} />
            <Stat label="Vendas pelos anúncios" value={formatNumber(d.conversions)} />
          </div>
          <p className="text-sm text-muted">{d.comment}</p>
        </div>
      );
    default:
      return <p className="text-sm text-muted">{typeof d.value === 'number' ? formatNumber(d.value) : 'Abra o módulo para ver os detalhes.'}</p>;
  }
}

export function DashboardPage() {
  const api = useApi();
  const { tenant } = useCurrent();
  const [params, setParams] = useSearchParams();
  const range = (RANGES.find((r) => r.key === params.get('periodo'))?.key ?? 'month') as Range;
  const q = useQuery({
    queryKey: ['dashboard', range],
    queryFn: () => api.get<DashboardResponse>('/v1/core/dashboard', { query: { range } }),
  });
  const widgets = q.data?.widgets ?? [];
  const main = widgets.filter((w) => w.kind !== 'upsell');
  const upsell = widgets.filter((w) => w.kind === 'upsell');

  return (
    <div>
      <PageHeader
        title={`Olá${tenant ? `, ${tenant.tradeName}` : ''}`}
        description="O resumo do seu negócio."
        actions={<Tabs items={RANGES} value={range} onChange={(k) => setParams({ periodo: k })} />}
      />
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <Alert tone="danger">{errorMessage(q.error)}</Alert>
      ) : (
        <div className="space-y-6">
          {q.data.failed.length > 0 && (
            <Alert tone="warning">
              Não conseguimos carregar: {q.data.failed.map((k) => MODULE_CATALOG[k as ModuleKey]?.name ?? k).join(', ')}. Tente de novo em instantes.
            </Alert>
          )}
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {main.map((w) => (
              <Card key={w.key} className={cn(w.key === 'core.checklist' && 'md:row-span-2')}>
                <CardHeader title={w.title} />
                <CardBody>
                  <WidgetBody w={w} />
                </CardBody>
              </Card>
            ))}
          </div>
          {upsell.length > 0 && (
            <section aria-labelledby="upsell-title">
              <h2 id="upsell-title" className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted">
                Mais para o seu negócio
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {upsell.map((w) => {
                  const module = (w.data as { module: ModuleKey }).module;
                  return (
                    <Card key={w.key}>
                      <CardBody className="space-y-2">
                        <p className="font-medium">{w.title}</p>
                        <p className="text-sm text-muted">{(w.data as { description: string }).description}</p>
                        <WidgetLink to={`/assinatura?adicionar=${module}`}>Ative para ver</WidgetLink>
                      </CardBody>
                    </Card>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
